import { useEffect, useRef, useState } from 'react'
import { useInView, useReducedMotion } from 'framer-motion'
import { useLanguage } from '../../contexts/LanguageContext'

type CountUpProps = {
  to: number
  suffix?: string
  locale: string
}

// One-shot count-up: a short rAF loop that starts once the number scrolls into
// view and stops at the target (skipped entirely under reduced motion).
const CountUp = ({ to, suffix = '', locale }: CountUpProps) => {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, margin: '-40px' })
  const reduce = useReducedMotion()
  const [value, setValue] = useState(0)

  useEffect(() => {
    if (!inView) return
    if (reduce) {
      setValue(to)
      return
    }
    const duration = 1300
    const start = performance.now()
    let raf = 0
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration)
      // ease-out cubic so the last digits settle gently
      setValue(Math.round(to * (1 - Math.pow(1 - p, 3))))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [inView, reduce, to])

  return (
    <span ref={ref} className="tabular-nums">
      {value.toLocaleString(locale)}
      {suffix}
    </span>
  )
}

const StatsBand = () => {
  const { language } = useLanguage()
  const isKa = language === 'ka'
  const locale = isKa ? 'ka-GE' : 'en-US'

  // Product facts only — no usage metrics we can't back up yet.
  const stats = [
    {
      to: 3,
      suffix: '',
      label: isKa ? 'წუთი პირველ მოსაწვევამდე' : 'minutes to your first invite',
    },
    {
      to: 9,
      suffix: '+',
      label: isKa ? 'ღონისძიების ტიპი' : 'event types to pick from',
    },
    {
      to: 24,
      suffix: '/7',
      label: isKa ? 'ხელმისაწვდომი ერთი ბმულით' : 'available from a single link',
    },
    {
      to: 2,
      suffix: '',
      label: isKa ? 'ენა ყველა მოსაწვევში' : 'languages in every invite',
    },
  ]

  // Full-width band in the one brand accent (celebration pink — the colour
  // the hero's highlighted word is set in): white count-up numbers over soft
  // white glow blobs. It used to be the juicy gradient (mockup .stats).
  return (
    <section className="relative overflow-hidden bg-celebration-pink text-white">
      <div
        aria-hidden="true"
        className="blob"
        style={{ width: 400, height: 400, background: '#fff', top: -150, left: '10%', opacity: 0.25, filter: 'blur(50px)' }}
      />
      <div
        aria-hidden="true"
        className="blob"
        style={{ width: 360, height: 360, background: '#fff', bottom: -150, right: '10%', opacity: 0.25, filter: 'blur(50px)' }}
      />
      <div className="relative mx-auto grid max-w-6xl grid-cols-2 gap-x-6 gap-y-10 px-5 py-16 text-center lg:grid-cols-4 lg:py-20">
        {stats.map((s) => (
          <div key={s.label}>
            <p className="font-display text-[clamp(2.2rem,4.5vw,3.4rem)] font-extrabold leading-none tracking-tight text-white">
              <CountUp to={s.to} suffix={s.suffix} locale={locale} />
            </p>
            <p className="mt-2 text-sm font-semibold text-white/90">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

export default StatsBand
